import { useMemo, useState } from 'react';
import { ListFilter } from 'lucide-react';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  isComboboxDismissal,
  MultiIdCombobox,
} from '@/components/ui/multi-id-combobox';
import { Ics213RrList } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrList';

const ResourceRequestView = ({
  hub = false,
}: {
  /** Hub lists every workspace and cannot create or edit a request. */
  hub?: boolean;
}) => {
  const [workspaceIds, setWorkspaceIds] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { data: incidents = [] } = useAllIncidents();

  const workspaceOptions = useMemo(
    () =>
      incidents.map((incident) => ({
        value: incident.id,
        label: incident.name,
      })),
    [incidents]
  );

  const hasWorkspaceFilter = workspaceIds.length > 0;

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {hub ? (
        <div className="tw:flex tw:justify-end">
          <Popover
            open={filtersOpen}
            onOpenChange={(open, eventDetails) => {
              const fromCombobox =
                !open &&
                (eventDetails.reason === 'outside-press' ||
                  eventDetails.reason === 'focus-out') &&
                isComboboxDismissal(eventDetails.event);
              if (fromCombobox) {
                eventDetails.cancel();
                return;
              }
              setFiltersOpen(open);
            }}
          >
            <PopoverTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  aria-label="Filters"
                  aria-expanded={filtersOpen}
                  className="tw:relative"
                >
                  <ListFilter className="tw:h-4 tw:w-4" />
                  {hasWorkspaceFilter ? (
                    <span
                      className="tw:absolute tw:right-1 tw:top-1 tw:size-1.5 tw:rounded-full tw:bg-primary"
                      aria-hidden
                    />
                  ) : null}
                </Button>
              }
            />
            <PopoverContent align="end" className="dark tw:w-80 tw:p-3">
              <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
                Workspace
              </div>
              <MultiIdCombobox
                options={workspaceOptions}
                value={workspaceIds}
                onChange={setWorkspaceIds}
                placeholder="All workspaces"
              />
            </PopoverContent>
          </Popover>
        </div>
      ) : null}
      <Ics213RrList hub={hub} readOnly={hub} workspaceIds={workspaceIds} />
    </div>
  );
};

export default ResourceRequestView;
